import _, { cloneDeep, isEqual } from "lodash";
import { DEFAULT_DELIVERY_COST, FREE_DELIVERY_FROM_PRICE } from "../constants/DeliveryConstants";
import dayjs from "dayjs";
import {
  ADDITIONAL_DELIVERY_LABEL,
  ORDER_PICKING_STATUS_COMPLETED,
  ORDER_PICKING_STATUS_IN_PROGRESS,
  ORDER_STATUS_CREATED,
  ORDER_STATUS_PACKED,
  SOO_COOL_DELIVERY_LABELS,
} from "../constants/OrderConstant";
import axios from "axios";
import { sendEmailOrderUpdate } from "../urls";
import { calculateOrderPricing } from "./pricing";
import { canUnpackOrder, createDraftInvoice, deleteDraftInvoice } from "./invoices";
import { updatePackagingStockDelta } from "./packagingStock";
import {
  FIRESTORE_ORDERPICKINGS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../constants/CompanyConstants";
import { firestore } from "../configs/FirebaseConfig";
import { deleteField, doc, getDoc, updateDoc } from "firebase/firestore";

export const getOrderAmountForCompany = (allOrders, customerId) => {
  const ordersForCustomer = _.filter(allOrders, (order) => order.farmorderCustomerId === customerId);
  return ordersForCustomer.length;
};

export const getDiscountForOrder = (price, percentage) => {
  return percentage > 0 ? price - (price - price * (percentage / 100)) : null;
};

export const calculateShippingCost = (subtotal, customerId, customers, deliveryDefaults = null) => {
  if (!customerId || !customers) return 0;
  let customerDeliveryCost = deliveryDefaults?.defaultDeliveryCost ?? DEFAULT_DELIVERY_COST;
  let customerDeliveryThreshold = deliveryDefaults?.deliveryCostThreshold ?? FREE_DELIVERY_FROM_PRICE;
  const dbCustomer = customers.find((customer) => customer.id === customerId);
  if (
    dbCustomer &&
    typeof dbCustomer.deliveryCost === "number" &&
    typeof dbCustomer.deliveryCostThreshold === "number"
  ) {
    customerDeliveryCost = dbCustomer.deliveryCost;
    customerDeliveryThreshold = dbCustomer.deliveryCostThreshold;
  }
  return subtotal >= customerDeliveryThreshold ? 0 : customerDeliveryCost;
};

export const calculateDiscount = (price, percentage) => {
  return percentage > 0 ? price - (price - price * (percentage / 100)) : 0;
};

/**
 * Prepare shopping cart update data for an order
 * @param {Object} order - Original order object
 * @param {Array} newShoppingCart - New shopping cart items
 * @param {Object} auth - Authentication object with email
 * @param {boolean} deliveryDateChanged - Whether delivery date has changed
 * @returns {Object} Update object with cart changes and metadata
 */
export const prepareShoppingCartUpdate = (order, newShoppingCart, email, deliveryDateChanged = false) => {
  const shoppingCartChanged = !isEqual(order.userShoppingCart, newShoppingCart);

  if (!shoppingCartChanged && !deliveryDateChanged) {
    return { hasChanges: false };
  }

  const updateObject = {};

  // Prepare previous shopping carts history
  const previousShoppingCarts = order.previousShoppingCarts?.length > 0 ? cloneDeep(order.previousShoppingCarts) : [];

  if (shoppingCartChanged || deliveryDateChanged) {
    // Add current state to history
    const { orderDateTime, shippingCost, subTotalAmount, tax, totalAmount, crates, farmorderDeliveryDate } = order;

    previousShoppingCarts.push({
      timestamp: dayjs().unix(),
      items: order.userShoppingCart,
      orderDateTime,
      shippingCost,
      subTotalAmount,
      tax: tax || 0,
      totalAmount,
      crates: crates || null,
      farmorderDeliveryDate,
      updatedBy: email,
    });

    updateObject.previousShoppingCarts = previousShoppingCarts;
    updateObject.updatedAt = dayjs().unix();
    updateObject.updatedBy = email;
  }

  if (shoppingCartChanged) {
    // Clean up the new shopping cart before saving
    // Remove changeRequestedBy and changeReason from products that weren't changed
    const cleanedCart = newShoppingCart.map((item) => {
      const originalItem = order.userShoppingCart.find((orig) => orig.productId === item.productId);
      const quantityChanged = !originalItem || originalItem.itemCount !== item.itemCount;

      // Only keep changeRequestedBy and changeReason if quantity actually changed
      if (quantityChanged && item.changeRequestedBy) {
        return item;
      } else {
        // Remove these fields if they exist but shouldn't
        const { changeRequestedBy, changeReason, ...cleanItem } = item;
        return cleanItem;
      }
    });

    updateObject.userShoppingCart = cleanedCart;
  }

  return {
    hasChanges: true,
    shoppingCartChanged,
    updateObject,
  };
};

/**
 * Handle SooCool order changes (create, update, cancel)
 * @param {Object} order - Original order object
 * @param {string} newDeliveryLabel - New delivery label
 * @param {Object} changes - Object with change flags and new order data
 * @param {Function} handleSendToSooCool - Function to send orders to SooCool
 * @param {Function} cancelOrderInSooCool - Function to cancel SooCool orders
 * @returns {Object} Operation result with success/error status
 */
export const handleSooCoolOrderChanges = async (
  order,
  newDeliveryLabel,
  changes,
  handleSendToSooCool,
  cancelOrderInSooCool,
) => {
  const result = {
    attempted: false,
    success: false,
    error: null,
  };

  const {
    deliveryLabelChanged,
    deliveryDateChanged,
    cratesChanged,
    scannedCratesChanged,
    shoppingCartChanged,
    newOrderData,
  } = changes;

  const oldLabelIsSooCool = SOO_COOL_DELIVERY_LABELS.includes(order.deliveryLabel || order.customer?.deliveryLabel);
  const newLabelIsSooCool = SOO_COOL_DELIVERY_LABELS.includes(newDeliveryLabel);
  const sooCoolRelevantChange = oldLabelIsSooCool || newLabelIsSooCool;

  // Special case: if the order becomes an additional delivery (ADD), we must cancel the existing SooCool delivery
  // instead of updating it. The physical packing (boxes) stays tied to the original SooCool order.
  if (oldLabelIsSooCool && newDeliveryLabel === ADDITIONAL_DELIVERY_LABEL && order.sooCoolId) {
    result.attempted = true;
    try {
      await cancelOrderInSooCool(order.sooCoolId, order.id);
      result.success = true;
      return result;
    } catch (error) {
      console.error("SooCool cancellation error (additional delivery):", error);
      result.error = error.message;
      return result;
    }
  }

  // Case 1: Delivery label changed and involves SooCool
  if (deliveryLabelChanged && sooCoolRelevantChange) {
    result.attempted = true;

    // Scenario A: Switching FROM SooCool TO non-SooCool (cancel)
    if (
      SOO_COOL_DELIVERY_LABELS.includes(order.deliveryLabel) &&
      !SOO_COOL_DELIVERY_LABELS.includes(newDeliveryLabel) &&
      order.sooCoolId
    ) {
      try {
        await cancelOrderInSooCool(order.sooCoolId, order.id);
        result.success = true;
      } catch (error) {
        console.error("SooCool cancellation error:", error);
        result.error = error.message;

        const isTimeLockedError =
          error.message && (error.message.includes("17:00") || error.message.toLowerCase().includes("locked"));

        if (isTimeLockedError && shoppingCartChanged) {
          result.success = true;
          result.error = "Time locked - delivery changes skipped, products updated";
          return { ...result, skipDeliveryLabelUpdate: true };
        }

        return result;
      }
    }

    // Scenario B: Switching FROM non-SooCool TO SooCool (create)
    else if (
      !SOO_COOL_DELIVERY_LABELS.includes(order.deliveryLabel) &&
      SOO_COOL_DELIVERY_LABELS.includes(newDeliveryLabel)
    ) {
      try {
        await handleSendToSooCool(newOrderData, "create");
        result.success = true;
      } catch (error) {
        console.error("SooCool creation error:", error);
        result.error = error.message;
        return result;
      }
    }
  }

  // Case 2: Order is already SooCool and needs update (date/label/crates changed)
  console.log("[SooCool debug] handleSooCoolOrderChanges Case 2 condition check", {
    orderDeliveryLabel: order.deliveryLabel || order.customer?.deliveryLabel,
    newDeliveryLabel,
    deliveryDateChanged,
    deliveryLabelChanged,
    cratesChanged,
    scannedCratesChanged,
    isSooCoolCurrent: SOO_COOL_DELIVERY_LABELS.includes(order.deliveryLabel || order.customer?.deliveryLabel),
    isSooCoolNew: SOO_COOL_DELIVERY_LABELS.includes(newDeliveryLabel),
  });
  if (
    SOO_COOL_DELIVERY_LABELS.includes(order.deliveryLabel || order.customer?.deliveryLabel) &&
    SOO_COOL_DELIVERY_LABELS.includes(newDeliveryLabel) &&
    (deliveryDateChanged || deliveryLabelChanged || cratesChanged || scannedCratesChanged)
  ) {
    if (!result.attempted) {
      result.attempted = true;
    }

    try {
      // If the initial SooCool creation failed, there is nothing to update yet.
      // In that case treat this as a new creation, similar to AddOrderModal.
      const hasSooCoolDeliveryToUpdate = !!order.sooCoolId;
      console.log("[SooCool debug] calling handleSendToSooCool", {
        action: hasSooCoolDeliveryToUpdate ? "update" : "create",
        sooCoolId: order.sooCoolId,
      });
      const response = await handleSendToSooCool(newOrderData, hasSooCoolDeliveryToUpdate ? "update" : "create");
      console.log("[SooCool debug] handleSendToSooCool response", response?.data);
      result.success = true;

      if (response?.data?.skipped && response.data?.reason === "cutoff_time_passed") {
        result.cutoffTimePassed = true;
        result.error = "Cutoff time passed - SooCool was not updated, order changes were saved";
      }
    } catch (error) {
      console.error("SooCool update error:", error);
      result.error = error.message;

      const isTimeLockedError =
        error.message && (error.message.includes("17:00") || error.message.toLowerCase().includes("locked"));

      if (isTimeLockedError && shoppingCartChanged) {
        result.success = true;
        result.error = "Time locked - delivery changes skipped, products updated";
        return {
          ...result,
          skipDeliveryLabelUpdate: deliveryLabelChanged,
          revertCrates: cratesChanged,
          revertScannedCrates: scannedCratesChanged,
        };
      }

      return result;
    }
  }

  return result;
};

/**
 * Send order update notification via Email
 * @param {Object} order - Original order object
 * @param {Object} changes - Object with change information
 * @param {Array} users - Array of user objects
 * @param {Array} customers - Array of customer objects
 * @param {string} token - Firebase auth token
 * @returns {Object} Operation result with success/error status
 */
export const sendOrderUpdateNotification = async (order, changes, users, customers, token) => {
  const result = {
    attempted: true,
    success: false,
    error: null,
  };

  try {
    const { shoppingCartChanged, deliveryDateChanged, newOrderData, updatedOrderProducts, originalShoppingCart } =
      changes;

    const user = users.find((u) => u.userId === order.farmorderUserId);
    const customer = customers.find((c) => c.id === order.farmorderCustomerId);

    // Skip if no user or phone number
    if (!user || !user.userPhoneNumber) {
      result.attempted = false;
      return result;
    }

    // Only quantity/add/remove changes are considered meaningful for notification purposes.
    // Price-only changes (manual override or price list changes) never trigger the email on their own,
    // since there's no way for a CMS user to mark a price-only change as customer-requested.
    let meaningfulCartChange = false;

    if (shoppingCartChanged) {
      // Find all products that were actually changed (quantity, new, or removed)
      const changedProducts = [];

      // Check for updated or removed products
      originalShoppingCart.forEach((originalItem) => {
        const newItem = updatedOrderProducts.find((item) => item.productId === originalItem.productId);

        if (!newItem) {
          // Product was removed
          changedProducts.push({
            productId: originalItem.productId,
            hasCustomerRequest: false,
          });
        } else {
          const quantityChanged = originalItem.itemCount !== newItem.itemCount;

          if (quantityChanged) {
            changedProducts.push({
              productId: newItem.productId,
              hasCustomerRequest: newItem.changeRequestedBy === "customer",
            });
          }
        }
      });

      // Check for new products
      updatedOrderProducts.forEach((newItem) => {
        const originalItem = originalShoppingCart.find((item) => item.productId === newItem.productId);
        if (!originalItem) {
          changedProducts.push({
            productId: newItem.productId,
            hasCustomerRequest: newItem.changeRequestedBy === "customer",
          });
        }
      });

      meaningfulCartChange = changedProducts.length > 0;

      // If there are changed products, check if ALL of them have customer request
      if (changedProducts.length > 0) {
        const allChangesHaveCustomerRequest = changedProducts.every((p) => p.hasCustomerRequest);

        if (allChangesHaveCustomerRequest) {
          result.attempted = false;
          return result;
        }
      }
    }

    // Build base order update data
    const orderUpdateData = {
      farmorderId: order.farmorderId,
      orderNumber: order.exactOrderNumber || order.farmorderId,
      phoneNumber: user.userPhoneNumber,
      customerName: order.farmorderCustomerName,
      customerFirstName: order.farmorderUserFirstName,
      deliveryDate: newOrderData.farmorderDeliveryDate,
      currentItems: updatedOrderProducts,
      removedItems: [],
      updatedItems: [],
      newItems: [],
      customerEmail: user.userEmail,
      customerNumber: customer?.exactAccountCode || order.farmorderCustomerId,
      orderDateTime: order.orderDateTime,
      reference: order.reference,
      deliveryAddress: customer
        ? {
            street: customer.street,
            houseNumber: customer.houseNumber,
            houseNumberAddition: customer.houseNumberAddition,
            zipCode: customer.zipCode,
            city: customer.city,
            country: customer.country || "NL",
          }
        : null,
      userShoppingCart: updatedOrderProducts,
      previousShoppingCarts: newOrderData.previousShoppingCarts || order.previousShoppingCarts || [],
      deliveryLabel: newOrderData.deliveryLabel || order.deliveryLabel,
      farmorderCustomerId: order.farmorderCustomerId,
    };

    // Process shopping cart changes if applicable
    if (meaningfulCartChange) {
      const removedItems = originalShoppingCart.filter(
        (originalItem) => !updatedOrderProducts.find((newItem) => newItem.productId === originalItem.productId),
      );

      const updatedItems = updatedOrderProducts.filter((newItem) => {
        const originalItem = originalShoppingCart.find((item) => item.productId === newItem.productId);
        return originalItem && originalItem.itemCount !== newItem.itemCount;
      });

      const newItems = updatedOrderProducts.filter(
        (newItem) => !originalShoppingCart.find((item) => item.productId === newItem.productId),
      );

      orderUpdateData.removedItems = removedItems.map((item) => ({
        id: item.productId,
        name: item.productName || "Unknown Product",
        quantity: item.itemCount,
      }));

      orderUpdateData.updatedItems = updatedItems.map((newItem) => {
        const originalItem = originalShoppingCart.find((item) => item.productId === newItem.productId);
        return {
          id: newItem.productId,
          name: newItem.productName || "Unknown Product",
          oldQuantity: originalItem.itemCount,
          newQuantity: newItem.itemCount,
        };
      });

      orderUpdateData.newItems = newItems.map((item) => ({
        id: item.productId,
        name: item.productName || "Unknown Product",
        quantity: item.itemCount,
      }));
    }

    // Add delivery date change information
    if (deliveryDateChanged) {
      orderUpdateData.deliveryDateChanged = true;
      orderUpdateData.oldDeliveryDate = order.farmorderDeliveryDate;
      orderUpdateData.newDeliveryDate = newOrderData.farmorderDeliveryDate;
    }

    // Determine notification type
    let notificationType = null;
    if (deliveryDateChanged && meaningfulCartChange) {
      notificationType = "combinedUpdate";
    } else if (meaningfulCartChange) {
      notificationType = "orderUpdate";
    }

    // Format current items for the notification
    orderUpdateData.currentItems = orderUpdateData.currentItems.map((item) => ({
      id: item.productId,
      name: item.productName,
      quantity: item.itemCount,
      price: item.productPrice,
    }));

    // Send notification if there's a relevant change
    if (notificationType) {
      await axios.post(
        sendEmailOrderUpdate,
        {
          orderData: orderUpdateData,
          notificationType,
        },
        { headers: { Authorization: token } },
      );
      result.success = true;
    } else {
      // If no relevant changes, mark as not attempted
      result.attempted = false;
    }

    return result;
  } catch (error) {
    console.error("Failed to send email notification:", error);
    result.error = error.message;
    return result;
  }
};

/**
 * Calculate order pricing for database update
 * @param {Array} orderProducts - Array of order product objects
 * @param {Object} customer - Customer object
 * @param {string} deliveryLabel - Delivery label
 * @param {boolean} addShippingCost - Whether to add shipping cost
 * @param {Object} discountData - Discount code data
 * @param {Array} allProducts - Array of all available products
 * @param {Object} currentOrder - Current order for delivery charge fallback
 * @param {number} deliveryTimeCharge - New delivery time charge (if changed)
 * @param {object} priceLists - Optional price lists data (from usePriceLists hook)
 * @param {object|null} countriesMap - Optional map of country data keyed by country code (from useCountries hook)
 * @returns {Object} Object with all pricing fields for database update
 */
export const calculateOrderUpdatePricing = (
  orderProducts,
  customer,
  deliveryLabel,
  addShippingCost,
  discountData,
  allProducts,
  currentOrder,
  deliveryTimeCharge = null,
  priceLists = null,
  countriesMap = null,
) => {
  // Calculate all pricing components
  const pricing = calculateOrderPricing(
    orderProducts,
    customer,
    deliveryLabel,
    allProducts,
    addShippingCost,
    discountData,
    priceLists,
    countriesMap,
  );

  // Determine delivery time charge (use new value if provided, otherwise fall back to current order)
  const finalDeliveryTimeCharge =
    deliveryTimeCharge !== null ? deliveryTimeCharge : currentOrder.deliveryTimeCharge || 0;

  // Calculate final total including delivery time charge and discount
  const finalTotal = pricing.subtotal + pricing.shippingCost + finalDeliveryTimeCharge - pricing.discountAmount;

  return {
    shippingCost: pricing.shippingCost.toString(),
    subTotalAmount: pricing.subtotal,
    subTotalWithoutDiscount: pricing.subtotalWithoutDiscount,
    invoiceAdditionalCosts: pricing.invoiceAdditionalCosts,
    tax: pricing.tax,
    totalAmount: Math.max(0, finalTotal), // Ensure total is never negative
    shippingCostDisabled: !addShippingCost,
  };
};

/**
 * Ensures farmorderId is not longer than 8 characters before packing/invoicing
 * @param {Object} order - The order object to validate and potentially update
 * @param {number} maxLength - Maximum allowed length for farmorderId (default: 8)
 * @returns {Object} - { updatedOrder, hasChanged, updateFields }
 */
export const prepareFarmOrderIdForPacking = (order, maxLength = 8) => {
  if (!order || !order.farmorderId) {
    return {
      updatedOrder: order,
      hasChanged: false,
      updateFields: {},
    };
  }

  const currentId = order.farmorderId;

  // Check if truncation is needed
  if (currentId.length > maxLength) {
    const truncatedId = currentId.substring(0, maxLength);

    return {
      updatedOrder: {
        ...order,
        farmorderId: truncatedId,
      },
      hasChanged: true,
      updateFields: {
        farmorderId: truncatedId,
      },
    };
  }

  // No change needed
  return {
    updatedOrder: order,
    hasChanged: false,
    updateFields: {},
  };
};

/**
 * Handle order packing status change with all related logic
 * @param {Object} order - The order object
 * @param {boolean} packed - Whether to pack or unpack the order
 * @param {Object} options - Additional options
 * @param {number} options.crates - Number of crates (optional)
 * @param {string} options.updatedBy - Email of user making the change (optional)
 * @param {Array} options.scannedCrates - New scanned crates array (optional)
 * @param {Array} options.oldScannedCrates - Pre-change scanned crates array, only needed if `order.scannedCrates`
 *   already reflects the new value (optional, falls back to order.scannedCrates)
 * @param {boolean} shouldAutoEmail - Whether to trigger automatic print emails (default: false)
 * @returns {Object} Result with updateObject and metadata
 */
export const handleOrderPackingStatusChange = async (order, packed, options = {}, shouldAutoEmail = false) => {
  try {
    const isAcquisitionOrder = order.type === "acquisition";

    // If trying to unpack, first check if it's allowed (skip for acquisition orders)
    if (!packed && !isAcquisitionOrder) {
      const canUnpack = await canUnpackOrder(order.farmorderId || order.id);
      if (!canUnpack) {
        throw new Error("Cannot unpack order with completed invoice");
      }
    }

    // Get hasPickingDocument from options (passed from component with useOrderPickings hook)
    const hasPickingDocument = options.hasPickingDocument || false;

    let invoiceId = null;
    let updateObject = {
      packed,
      farmorderStatus: packed ? ORDER_STATUS_PACKED : ORDER_STATUS_CREATED,
    };

    // Add administrativelyPacked flag if packing without picking document
    if (packed && !hasPickingDocument) {
      updateObject.administrativelyPacked = true;
    } else if (!packed) {
      // Remove flag when unpacking
      updateObject.administrativelyPacked = deleteField();
    }

    // Add crates if provided
    if (options.crates !== undefined) {
      updateObject.crates = options.crates;
    }

    // Add updatedBy if provided
    if (options.updatedBy) {
      updateObject.updatedBy = options.updatedBy;
      updateObject.updatedAt = dayjs().unix();
    }

    // Check and truncate farmorderId if necessary
    let orderForInvoice = order;
    if (packed) {
      const farmOrderResult = prepareFarmOrderIdForPacking(order);
      if (farmOrderResult.hasChanged) {
        Object.assign(updateObject, farmOrderResult.updateFields);
      }
      orderForInvoice = farmOrderResult.updatedOrder;
    }

    // Handle invoice logic - skip for acquisition orders
    if (!isAcquisitionOrder) {
      if (packed) {
        invoiceId = await createDraftInvoice(orderForInvoice);
        updateObject.invoiceId = invoiceId;
      } else {
        await deleteDraftInvoice(order.farmorderId);
        updateObject.invoiceId = null;
      }
    }

    // Update orderPicking document status
    await updateOrderPickingStatus(order.id, packed, options.updatedBy);

    // Update packaging stock only when packing (not unpacking)
    if (packed) {
      // Callers that merge the new scannedCrates into `order` before calling this function
      // (e.g. orderpicking-detail) must pass the pre-merge value via options.oldScannedCrates,
      // otherwise old/new would both resolve to the new value and the delta would be 0.
      const oldScannedCrates = options.oldScannedCrates || order.scannedCrates || [];
      const newScannedCrates = options.scannedCrates || order.scannedCrates || [];

      try {
        const stockResult = await updatePackagingStockDelta(oldScannedCrates, newScannedCrates, options.updatedBy);
        if (!stockResult.success) {
          console.error("Failed to update packaging stock:", stockResult.error);
          // Continue anyway - don't block the packing operation
        }
      } catch (error) {
        console.error("Error updating packaging stock:", error);
        // Continue anyway - don't block the packing operation
      }
    }

    return {
      success: true,
      updateObject,
      invoiceId,
      orderForInvoice,
      shouldAutoEmail,
    };
  } catch (error) {
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Update orderPicking document status when order packing status changes
 * @param {string} orderId - The order ID
 * @param {boolean} packed - Whether order is packed or unpacked
 * @param {string} userEmail - The email of the user making the change
 * @returns {Promise<void>}
 */
export const updateOrderPickingStatus = async (orderId, packed, userEmail = null) => {
  try {
    const orderPickingRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_ORDERPICKINGS_TABLE,
      orderId,
    );

    const orderPickingDoc = await getDoc(orderPickingRef);

    if (orderPickingDoc.exists()) {
      if (packed) {
        // When packing, set to completed
        await updateDoc(orderPickingRef, {
          status: ORDER_PICKING_STATUS_COMPLETED,
          completedAt: dayjs().unix(),
          updatedAt: dayjs().unix(),
          updatedBy: userEmail,
        });
      } else {
        // When unpacking, update originalShoppingCart AND sync requestedQuantity
        const orderRef = doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, orderId);
        const currentOrder = await getDoc(orderRef);
        const currentCart = currentOrder.data().userShoppingCart;

        // Update pickedItems met nieuwe requestedQuantity values
        const orderPickingData = orderPickingDoc.data();
        const updatedPickedItems = orderPickingData.pickedItems.map((item) => {
          const currentProduct = currentCart.find((p) => p.productId === item.productId);
          return {
            ...item,
            requestedQuantity: currentProduct ? currentProduct.itemCount : 0,
          };
        });

        const updateData = {
          status: ORDER_PICKING_STATUS_IN_PROGRESS,
          updatedAt: dayjs().unix(),
          updatedBy: userEmail,
          originalShoppingCart: currentCart,
          pickedItems: updatedPickedItems, // Update requested quantities
        };

        await updateDoc(orderPickingRef, updateData);
        await updateDoc(orderPickingRef, {
          completedAt: deleteField(),
        });
      }
    }
  } catch (error) {
    console.error("Error updating orderPicking status:", error);
  }
};

/**
 * Update orderPicking document when an order is edited while being picked
 * @param {string} orderId - The order ID
 * @param {Array} newShoppingCart - The updated shopping cart
 * @param {Object} orderUpdates - Optional order updates
 * @param {string} userEmail - The email of the user making the change
 * @returns {Promise<void>}
 */
export const updateOrderPickingForEditedOrder = async (
  orderId,
  newShoppingCart,
  orderUpdates = {},
  userEmail = null,
) => {
  try {
    const orderPickingRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_ORDERPICKINGS_TABLE,
      orderId,
    );

    const orderPickingDoc = await getDoc(orderPickingRef);

    if (!orderPickingDoc.exists()) {
      console.warn("No orderPicking document found for order:", orderId);
      return;
    }

    const orderPickingData = orderPickingDoc.data();
    const currentPickedItems = orderPickingData.pickedItems || [];

    // Create a map of new shopping cart items for easy lookup
    const newCartMap = new Map(newShoppingCart.map((item) => [item.productId, item]));

    // Filter out items that are no longer in the order
    const filteredPickedItems = currentPickedItems.filter((pickedItem) => newCartMap.has(pickedItem.productId));

    // Update requestedQuantity for existing items
    const updatedPickedItems = filteredPickedItems.map((pickedItem) => {
      const newCartItem = newCartMap.get(pickedItem.productId);
      return {
        ...pickedItem,
        requestedQuantity: newCartItem.itemCount,
        isReady: newCartItem.itemCount === 0 ? true : pickedItem.isReady,
      };
    });

    // Add new items that weren't in the original picking list
    const existingProductIds = new Set(currentPickedItems.map((item) => item.productId));
    const newItems = newShoppingCart
      .filter((cartItem) => !existingProductIds.has(cartItem.productId))
      .map((cartItem) => ({
        productId: cartItem.productId,
        productName: cartItem.productName,
        requestedQuantity: cartItem.itemCount,
        pickedQuantity: 0,
        isReady: cartItem.itemCount === 0,
      }));

    // Combine updated existing items with new items
    const finalPickedItems = [...updatedPickedItems, ...newItems];

    // Build update object
    const updateData = {
      originalShoppingCart: newShoppingCart,
      pickedItems: finalPickedItems,
      updatedAt: dayjs().unix(),
      updatedBy: userEmail,
    };

    // Add optional fields if provided
    if (orderUpdates.deliveryLabel !== undefined) {
      updateData.deliveryLabel = orderUpdates.deliveryLabel;
    }
    if (orderUpdates.crates !== undefined) {
      updateData.crates = orderUpdates.crates;
    }
    if (orderUpdates.farmorderDeliveryDate !== undefined) {
      updateData.farmorderDeliveryDate = orderUpdates.farmorderDeliveryDate;
    }
    if (orderUpdates.internalNotes !== undefined) {
      updateData.internalNotes = orderUpdates.internalNotes;
    }
    if (orderUpdates.orderCommentsProcessed !== undefined) {
      updateData.orderCommentsProcessed = orderUpdates.orderCommentsProcessed;
    }

    // Update the orderPicking document
    await updateDoc(orderPickingRef, updateData);
  } catch (error) {
    console.error("Error updating orderPicking for edited order:", error);
    throw error;
  }
};
